import React, { useState } from 'react';
import {
    Pencil,
    Trash2,
    PlusCircle,
    Zap,
    Settings,
    Play,
    Pause,
    Eye,
    Copy,
    Target,
    MessageCircle,
    Hash,
    Save
} from 'lucide-react';
import ConversationGraphBuilderForm from './ConversationGraphBuilder';

// Type definitions
export type Theme = 'dark' | 'light';

export interface ThemeColors {
    primary: string;
    secondary: string;
    background: string;
    surface: string;
    border: string;
    text: {
        primary: string;
        secondary: string;
        muted: string;
    };
    accent: string;
    hover: string;
    input: string;
}

interface Intent {
    id: string;
    name: string;
    description: string;
    status: 'active' | 'inactive';
    confidence: number;
    trainingPhrases: number;
    responses: number;
    lastTrained: string;
    createdAt: string;
    triggers: number;
}

interface IntentFormData {
    name: string;
    description: string;
    trainingPhrases: string[];
    responses: string[];
}

export interface ChatbotIntentsListingProps {
    theme?: Theme;
}

// Theme Configuration
const THEME_CONFIG: Record<Theme, ThemeColors> = {
    dark: {
        primary: 'bg-neutral-900',
        secondary: 'bg-neutral-800',
        background: 'bg-neutral-950',
        surface: 'bg-neutral-800',
        border: 'border-neutral-700',
        text: {
            primary: 'text-white',
            secondary: 'text-neutral-300',
            muted: 'text-neutral-400'
        },
        accent: 'bg-indigo-600',
        hover: 'hover:bg-neutral-800',
        input: 'bg-neutral-700 border-neutral-600 text-white'
    },
    light: {
        primary: 'bg-white',
        secondary: 'bg-gray-50',
        background: 'bg-gray-100',
        surface: 'bg-white',
        border: 'border-gray-200',
        text: {
            primary: 'text-gray-900',
            secondary: 'text-gray-700',
            muted: 'text-gray-500'
        },
        accent: 'bg-indigo-600',
        hover: 'hover:bg-gray-100',
        input: 'bg-white border-gray-300 text-gray-900'
    }
};

const ChatbotIntentsListing: React.FC<ChatbotIntentsListingProps> = ({
    theme = 'dark'
}) => {
    const colors = THEME_CONFIG[theme];

    const [showNewIntent, setshowNewIntent] = useState<boolean>(false);
    const [intents, setIntents] = useState<Intent[]>([
        {
            id: '1',
            name: 'Greeting',
            description: 'Handles user greetings and welcome messages',
            status: 'active',
            confidence: 0.95,
            trainingPhrases: 12,
            responses: 8,
            lastTrained: '2025-01-15T10:30:00Z',
            createdAt: '2025-01-10T08:00:00Z',
            triggers: 1240
        },
        {
            id: '2',
            name: 'Order Status',
            description: 'Helps users check their order status and tracking information',
            status: 'active',
            confidence: 0.87,
            trainingPhrases: 25,
            responses: 15,
            lastTrained: '2025-01-14T15:20:00Z',
            createdAt: '2025-01-08T12:15:00Z',
            triggers: 876
        },
        {
            id: '3',
            name: 'Product Information',
            description: 'Provides detailed product specifications and features',
            status: 'inactive',
            confidence: 0.78,
            trainingPhrases: 18,
            responses: 12,
            lastTrained: '2025-01-12T09:45:00Z',
            createdAt: '2025-01-05T14:30:00Z',
            triggers: 543
        },
        {
            id: '4',
            name: 'Complaint Handling',
            description: 'Manages customer complaints and escalation procedures',
            status: 'active',
            confidence: 0.92,
            trainingPhrases: 31,
            responses: 20,
            lastTrained: '2025-01-16T11:15:00Z',
            createdAt: '2025-01-03T16:20:00Z',
            triggers: 234
        }
    ]);

    const handleCreateIntent = (): void => {
        // const intent: Intent = {
        //   id: Date.now().toString(),
        //   name: newIntent.name,
        //   description: newIntent.description,
        //   status: 'inactive',
        //   confidence: 0.0,
        //   trainingPhrases: newIntent.trainingPhrases.length,
        //   responses: newIntent.responses.length,
        //   lastTrained: new Date().toISOString(),
        //   createdAt: new Date().toISOString(),
        //   triggers: 0
        // };
        // setIntents(prev => [...prev, intent]);
        setshowNewIntent(false);
    };

    const toggleIntentStatus = (id: string): void => {
        setIntents(prev => prev.map(intent =>
            intent.id === id
                ? { ...intent, status: intent.status === 'active' ? 'inactive' : 'active' }
                : intent
        ));
    };

    const handleDeleteIntent = (id: string): void => {
        setIntents(prev => prev.filter(intent => intent.id !== id));
    };

    const formatConfidence = (confidence: number): string => {
        return `${(confidence * 100).toFixed(1)}%`;
    };

    return (
        <div className={`h-full ${colors.background} overflow-auto`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b ${colors.border} ${colors.primary} sticky top-0 z-10`}>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className={`text-lg font-semibold ${colors.text.primary}`}>Chatbot Intents</h1>
                        <p className={`text-sm ${colors.text.muted} mt-1`}>
                            Manage chatbot intents, training phrases, and response patterns for natural language understanding.
                        </p>
                    </div>

                    <button
                        onClick={() => setshowNewIntent(true)}
                        className={`px-4 py-2 ${colors.accent} hover:bg-indigo-700 text-white text-sm rounded-md transition-colors flex items-center gap-2 font-medium shadow-lg`}
                    >
                        <PlusCircle className="w-4 h-4" /> Add New Intent
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="p-6 max-w-7xl mx-auto">

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className={`${colors.surface} border ${colors.border} rounded-lg p-4`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className={`text-sm ${colors.text.muted}`}>Total Intents</p>
                                <p className={`text-2xl font-semibold ${colors.text.primary}`}>{intents.length}</p>
                            </div>
                            <Target className="w-8 h-8 text-indigo-500" />
                        </div>
                    </div>

                    <div className={`${colors.surface} border ${colors.border} rounded-lg p-4`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className={`text-sm ${colors.text.muted}`}>Active Intents</p>
                                <p className="text-2xl font-semibold text-green-400">
                                    {intents.filter(i => i.status === 'active').length}
                                </p>
                            </div>
                            <Play className="w-8 h-8 text-green-500" />
                        </div>
                    </div>

                    <div className={`${colors.surface} border ${colors.border} rounded-lg p-4`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className={`text-sm ${colors.text.muted}`}>Total Triggers</p>
                                <p className="text-2xl font-semibold text-blue-400">
                                    {intents.reduce((sum, intent) => sum + intent.triggers, 0)}
                                </p>
                            </div>
                            <Zap className="w-8 h-8 text-blue-500" />
                        </div>
                    </div>

                    <div className={`${colors.surface} border ${colors.border} rounded-lg p-4`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className={`text-sm ${colors.text.muted}`}>Avg Confidence</p>
                                <p className="text-2xl font-semibold text-purple-400">
                                    {formatConfidence(intents.reduce((sum, intent) => sum + intent.confidence, 0) / intents.length || 0)}
                                </p>
                            </div>
                            <Hash className="w-8 h-8 text-purple-500" />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className={`overflow-x-auto border ${colors.border} rounded-lg ${colors.surface}`}>
                    <table className={`min-w-full text-sm text-left ${colors.text.secondary}`}>
                        <thead className={`${colors.primary} ${colors.text.muted} border-b ${colors.border}`}>
                            <tr>
                                <th className="px-4 py-3 font-medium">Intent Name</th>
                                <th className="px-4 py-3 font-medium">Description</th>
                                <th className="px-4 py-3 font-medium">Confidence</th>
                                <th className="px-4 py-3 font-medium">Training Phrases</th>
                                <th className="px-4 py-3 font-medium">Responses</th>
                                <th className="px-4 py-3 font-medium">Triggers</th>
                                <th className="px-4 py-3 font-medium">Last Trained</th>
                                <th className="px-4 py-3 font-medium">Status</th>
                                <th className="px-4 py-3 text-right font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody className={colors.surface}>
                            {intents.map((intent) => (
                                <tr key={intent.id} className={`border-t ${colors.border} ${colors.hover} transition-colors`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                                                <Target className="w-4 h-4 text-white" />
                                            </div>
                                            <span className={`font-medium ${colors.text.primary}`}>{intent.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 max-w-xs">
                                        <span className={`${colors.text.secondary} truncate block`}>{intent.description}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-16 ${theme === 'dark' ? 'bg-neutral-700' : 'bg-gray-200'} rounded-full h-2`}>
                                                <div
                                                    className={`h-2 rounded-full transition-all ${intent.confidence >= 0.9 ? 'bg-green-500' :
                                                        intent.confidence >= 0.8 ? 'bg-yellow-500' : 'bg-red-500'
                                                        }`}
                                                    style={{ width: `${intent.confidence * 100}%` }}
                                                ></div>
                                            </div>
                                            <span className={`text-xs font-mono ${colors.text.muted}`}>{formatConfidence(intent.confidence)}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`${colors.secondary} ${colors.text.secondary} px-2 py-1 rounded text-xs font-medium`}>{intent.trainingPhrases}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`${colors.secondary} ${colors.text.secondary} px-2 py-1 rounded text-xs font-medium`}>{intent.responses}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`font-mono text-xs ${colors.text.secondary}`}>{intent.triggers.toLocaleString()}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`font-mono text-xs ${colors.text.muted}`}>
                                            {new Date(intent.lastTrained).toLocaleDateString()}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span
                                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${intent.status === 'active'
                                                ? 'bg-green-800 text-green-200 border border-green-700'
                                                : 'bg-red-800 text-red-200 border border-red-700'
                                                }`}
                                        >
                                            {intent.status === 'active' ? (
                                                <Play className="w-3 h-3" />
                                            ) : (
                                                <Pause className="w-3 h-3" />
                                            )}
                                            {intent.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => toggleIntentStatus(intent.id)}
                                                className={`p-1 rounded transition-colors ${intent.status === 'active'
                                                    ? 'text-red-400 hover:text-red-300 hover:bg-red-900/20'
                                                    : 'text-green-400 hover:text-green-300 hover:bg-green-900/20'
                                                    }`}
                                                title={intent.status === 'active' ? 'Deactivate' : 'Activate'}
                                            >
                                                {intent.status === 'active' ? (
                                                    <Pause className="w-4 h-4" />
                                                ) : (
                                                    <Play className="w-4 h-4" />
                                                )}
                                            </button>
                                            <button className={`p-1 rounded text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 transition-colors`} title="View Details">
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button className={`p-1 rounded ${colors.text.muted} hover:text-gray-300 ${colors.hover} transition-colors`} title="Copy Intent ID">
                                                <Copy className="w-4 h-4" />
                                            </button>
                                            <button className="p-1 rounded text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20 transition-colors" title="Train Intent">
                                                <Settings className="w-4 h-4" />
                                            </button>
                                            <button className="p-1 rounded text-orange-400 hover:text-orange-300 hover:bg-orange-900/20 transition-colors" title="Edit">
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteIntent(intent.id)}
                                                className="p-1 rounded text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Popup Modal */}
            {showNewIntent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => setshowNewIntent(false)}
                    ></div>

                    {/* Modal Content */}
                    <div className={`relative w-full h-full ${colors.surface} overflow-auto`}>
                        {/* Close Button */}
                        <div className="absolute top-4 right-4 z-10">
                            <button
                                onClick={() => setshowNewIntent(false)}
                                className={`p-2 rounded-full ${colors.hover} ${colors.text.primary} hover:bg-red-600 transition-colors`}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Form Content */}
                        <div className="w-full h-full">
                            <ConversationGraphBuilderForm />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatbotIntentsListing;